<template>
  <div class="apply-wrapper">
    <div class="price-wrapper">
      <div class="price-card">
        <v-layout justify-space-between align-end>
          <div>{{ $t('fellowship.price.price-to-pay') }}</div>
          <div class="price">{{ price }}</div>
        </v-layout>
        <div class="division"></div>
      </div>
      <div class="price-remark text-pre-wrap">
        <span class="d-none d-md-inline">{{ $t('fellowship.price.subscription-warning') }}</span>
        <span class="d-inline d-md-none">{{ $t('fellowship.price.subscription-warning-sp') }}</span>
      </div>
      <div class="payment-wrapper">
        <div class="payment-title">{{ $t('fellowship.payment.registered-email') }}</div>
        <div class="email-wrapper">
          <v-text-field
            v-model="email"
            solo
            flat
            outlined
            placeholder="Email"
            :error-messages="emailErrors"
          ></v-text-field>
        </div>
        <div class="payment-title">{{ $t('fellowship.payment.credit-or-debit') }}</div>
        <div class="credit-wrapper">
          <v-text-field
            v-model="name"
            :placeholder="$t('fellowship.payment.card-name-placeholder')"
            :error-messages="nameErrors"
          ></v-text-field>
          <CreditInput ref="credit" @valid-changed="changeCreditValid"></CreditInput>
        </div>
        <div class="payment-btn-wrapper">
          <button class="btn-payment btn-shrink" :disabled="!valid" @click="apply()">
            {{ $t('fellowship.payment.submit-card-info') }}
          </button>
        </div>
      </div>
    </div>

    <v-dialog v-model="dialog" persistent max-width="300px">
      <v-card class="text-center" pa-4>
        <v-card-title primary-title>Subscribing to Entry</v-card-title>
        <v-card-text>
          <v-progress-circular
            v-show="dialogLoading"
            indeterminate
            color="primary"
          ></v-progress-circular>
          <div :class="{ 'font-red': isDialogErrorFatal }" class="mt-2 text-left text-pre-wrap">
            <span>{{ dialogMessage }}</span>
          </div>
        </v-card-text>
        <v-card-actions v-show="dialogClosable">
          <v-spacer></v-spacer>
          <v-btn color="gray" @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const kDialogMessageLoading = 'Loading...';

import { RequestFellowship } from '../../../../requests/fellowship/requests';
import CreditInput from '../../../Stripe/CreditInput';

export default {
  components: {
    CreditInput,
  },
  props: {
    price: { type: String, default: () => '' },
    planKey: { type: String, default: () => '' },
    countryCode: { type: String, default: () => '' },
  },
  data() {
    return {
      email: '',
      name: '',
      emailErrors: [],
      nameErrors: [],
      creditValid: false,
      dialog: false,
      dialogClosable: false,
      dialogLoading: false,
      dialogMessage: kDialogMessageLoading,
      isDialogErrorFatal: false,
      dialogErrorMessage: this.$t('fellowship.payment.dialog-error-message'),
      dialogErrorMessageFatal: this.$t('fellowship.payment.dialog-error-message-fatal'),
      dialogSuccessMessage: this.$t('fellowship.payment.dialog-success-message'),
    };
  },
  computed: {
    valid() {
      return this.creditValid && this.name && this.email;
    },
  },
  watch: {
    email() {
      this.emailErrors = [];
    },

    name() {
      this.nameErrors = [];
    },
  },

  methods: {
    async apply() {
      if (!this.valid) {
        this.showFieldErrors();
        return;
      }

      this.dialog = true;
      this.dialogLoading = true;
      this.dialogClosable = false;
      this.dialogMessage = kDialogMessageLoading;

      try {
        const token = await this.$refs.credit.createToken(this.name);

        await RequestFellowship.subscribe(
          this.planKey,
          this.name,
          this.email,
          token.id,
          this.countryCode
        );

        this.dialogLoading = false;
        this.dialogClosable = true;
        this.dialogMessage = this.dialogSuccessMessage;
        this.clearField();
      } catch (e) {
        if (e.response) {
          if (e.response.status == 422) {
            this.dialog = false;
            this.isDialogErrorFatal = false;
            this.emailErrors = e.response.data.email;
            this.nameErrors = e.response.data.name;
          } else if (e.response.status === 500) {
            this.dialogLoading = false;
            this.dialogMessage = this.dialogErrorMessageFatal;
            this.dialogClosable = true;
            this.isDialogErrorFatal = true;
          } else {
            this.dialogLoading = false;
            this.dialogMessage = this.dialogErrorMessage;
            this.dialogClosable = true;
            this.isDialogErrorFatal = false;
          }
        } else {
          this.dialogLoading = false;
          this.dialogMessage = this.dialogErrorMessage;
          this.dialogClosable = true;
          this.isDialogErrorFatal = false;
          throw e;
        }
      }
    },

    changeCreditValid(valid) {
      this.creditValid = valid;
    },

    showFieldErrors() {
      if (!this.email) {
        this.emailErrors = ['Email is required.'];
      }

      if (!this.name) {
        this.nameErrors = ['Name is required.'];
      }
    },

    clearField() {
      this.name = '';
      this.email = '';
      this.emailErrors = [];
      this.nameErrors = [];
      this.$refs.credit.clear();
    },
  },
};
</script>

<style scoped>
.apply-wrapper {
  margin-top: 100px;
  position: relative;
}
.price-wrapper {
  width: 54%;
  margin: 0 auto;
}
.price-card {
  background-color: #fff;
  font-size: 2.5em;
  color: #625d53;
  font-weight: bold;
}
.price-card > .division {
  height: 2px;
  background-color: rgb(124, 124, 124);
  margin: 4px 0;
}
.price-remark {
  color: #858585;
  font-size: 16px;
  font-weight: 600;
  margin-top: 2px;
  text-align: center;
}
.payment-wrapper {
  padding-top: 84px;
}
.payment-title {
  font-size: 26px;
  color: #625d53;
  font-weight: bold;
  margin-top: 36px;
  padding: 0 32px;
}
.email-wrapper {
  padding: 0 20px;
  background-color: #fff;
  height: 48px;
}
.credit-wrapper {
  margin: 0 18px;
  background-color: #fff;
  padding: 0 28px 18px;
  border: solid 1px rgb(191, 191, 191);
  border-radius: 4px;
}
.payment-btn-wrapper {
  padding: 120px 0;
  text-align: center;
}
.btn-payment {
  background-image: url('/img/lp/lpBtnPrimaryNomal.png');
  background-size: cover;
  width: 480px;
  height: 90px;
  font-size: 36px;
  font-weight: bold;
  color: #fff;
  outline: none;
}
.btn-payment:hover:not(:disabled) {
  background-image: url('/img/lp/lpBtnPrimaryHover.png');
}
.btn-payment:disabled {
  opacity: 0.6;
}
@media (max-width: 767px) {
  .apply-wrapper {
    margin-top: 40px;
  }
  .apply-title {
    font-size: 28px;
  }
  .price-wrapper {
    width: 330px;
  }
  .price-card {
    margin-top: 20px;
    font-size: 16px;
    padding: 4px 10px;
  }
  .price-card .price {
    font-size: 1.4em;
  }
  .price-card > .division {
    height: 1px;
    margin-top: 2px;
  }
  .price-remark {
    font-size: 10px;
  }
  .payment-wrapper {
    padding: 30px 18px;
  }
  .payment-title {
    font-size: 16px;
    margin-top: 30px;
    padding: 0;
  }
  .payment-btn-wrapper {
    padding: 60px 0 0;
  }
  .email-wrapper {
    padding: 0;
  }
  .credit-wrapper {
    padding-left: 12px;
    padding-right: 12px;
    margin: 0;
  }
  .btn-payment {
    width: 240px;
    height: 45px;
    font-size: 18px;
  }
}
@media (max-width: 360px) {
  .price-wrapper {
    width: 300px;
  }
}
</style>
